import type { Profile } from '@/stores'

import { getSupabase, isSupabaseConfigured } from '@/utils/supabase'
import type { PendingQuizScore } from '@/utils/quiz_pending'

const firstNonEmpty = (...values: (string | null | undefined)[]): string | null => {
	for (const value of values) {
		const trimmed = value?.trim()
		if (trimmed) return trimmed
	}
	return null
}

export type QuizScoreRecord = QuizScoreRow & {
	id: string
	created_at: string
}

export type QuizScoreRow = {
	quiz_id: string
	correct: number
	wrong: number
	total: number
	completed_at: string
	locale: string
	customer_id: number
	customer_code: string
	barcode: string
	member_id: string
	name: string
	email: string | null
	mobile_phone: string | null
	referral_code: string | null
	card_type_name: string | null
	redeem_points: number | null
	lucky_draw_points: number | null
	gender: string | null
	birth_date: string | null
	id_number: string | null
	nationality: string | null
	occupation: string | null
	default_address: string | null
}

export const buildQuizScoreRow = (
	profile: Profile,
	score: PendingQuizScore
): QuizScoreRow => {
	const customerCode =
		firstNonEmpty(profile.code, profile.barcode, String(profile.id)) || String(profile.id)
	const barcode =
		firstNonEmpty(profile.barcode, profile.code, String(profile.id)) || String(profile.id)
	const memberId =
		firstNonEmpty(profile.member_id, profile.code, profile.barcode, String(profile.id)) ||
		String(profile.id)

	return {
		quiz_id: score.quizId,
		correct: score.correct,
		wrong: score.wrong,
		total: score.total,
		completed_at: score.completedAt,
		locale: score.locale,
		customer_id: profile.id,
		customer_code: customerCode,
		barcode,
		member_id: memberId,
		name: firstNonEmpty(profile.name) || 'Unknown',
		email: firstNonEmpty(profile.email),
		mobile_phone: firstNonEmpty(profile.phone?.mobile),
		referral_code: firstNonEmpty(profile.referral),
		card_type_name: profile.card_type?.name || null,
		redeem_points: profile.point?.redeem ?? null,
		lucky_draw_points: profile.point?.lucky_draw ?? null,
		gender: firstNonEmpty(profile.gender),
		birth_date: profile.birth_date || null,
		id_number: firstNonEmpty(profile.id_number),
		nationality: profile.nationality?.name || null,
		occupation: profile.occupation?.name || null,
		default_address:
			profile.address?.find(a => a.default)?.address || profile.address?.[0]?.address || null
	}
}

export const saveQuizScore = async (
	profile: Profile,
	score: PendingQuizScore
): Promise<void> => {
	if (!isSupabaseConfigured()) {
		throw new Error('Supabase is not configured')
	}

	const supabase = getSupabase()
	if (!supabase) {
		throw new Error('Supabase client unavailable')
	}

	const row = buildQuizScoreRow(profile, score)
	const { error } = await supabase.from('quiz_scores').insert(row)

	if (error) throw error
}

export const fetchQuizScores = async (options?: {
	quizId?: string
	limit?: number
}): Promise<QuizScoreRecord[]> => {
	if (!isSupabaseConfigured()) {
		throw new Error('Supabase is not configured')
	}

	const supabase = getSupabase()
	if (!supabase) {
		throw new Error('Supabase client unavailable')
	}

	let query = supabase
		.from('quiz_scores')
		.select('*')
		.order('correct', { ascending: false })
		.order('completed_at', { ascending: false })

	if (options?.quizId) {
		query = query.eq('quiz_id', options.quizId)
	}

	if (options?.limit) {
		query = query.limit(options.limit)
	}

	const { data, error } = await query

	if (error) throw error
	return (data || []) as QuizScoreRecord[]
}
